import { useQuery } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';

/**
 * The Phase 0 health page, kept and moved off `/`.
 *
 * Still the fastest way to answer "is the whole chain up" in one screen:
 * browser to Vite to nginx to PHP-FPM to MySQL, Redis and object storage.
 */
interface HealthResponse {
  ok: boolean;
  app: string;
  env: string;
  checks: Record<string, { ok: boolean; detail?: string }>;
}

export function StatusPage() {
  const { data, error, isPending } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/health', { anonymous: true }),
    refetchInterval: 10_000,
    // A 503 here is the answer, not a failure - a dependency is down and the
    // body says which. Retrying hides that.
    retry: false,
  });

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-12">
      <h1 className="text-xl font-semibold tracking-tight">Stack status</h1>

      <Card className="p-6">
        {isPending && <p className="text-soft">Checking...</p>}

        {error && <p className="text-negative">The API did not answer.</p>}

        {data && (
          <ul className="flex flex-col gap-3">
            {Object.entries(data.checks).map(([name, check]) => (
              <li key={name} className="flex items-baseline justify-between gap-4">
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={`inline-block h-2 w-2 rounded-full ${check.ok ? 'bg-positive' : 'bg-negative'}`}
                  />
                  <span className="capitalize">{name}</span>
                </span>
                <span className="text-right text-sm text-faint">{check.detail ?? (check.ok ? 'ok' : 'down')}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
